import { IconButton } from '@atlaskit/button/new';
import SearchIcon from '@atlaskit/icon/core/search';
import { useIsReducedMotion } from '@atlaskit/motion';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import {
  type KeyboardShortcut,
  useDebouncedCallback,
  useKeyboardShortcut,
} from '@databases/react';
import { SearchBar } from '@databases/search-bar/SearchBar';
import { container, getService } from '@databases/service-container';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { isApple } from '../../../utils';

export interface QuickSearchProps {
  value?: DatabaseViewSettings['search'];
  onChange: (value: DatabaseViewSettings['search']) => void;
  tooltip: React.FC<{ children: React.ReactNode }>;
  /**
   * Optional callback that fires when the search field expands or collapses.
   *
   * @param expanded - true when expanded, false when collapsed
   */
  onExpandedChange?: (expanded: boolean) => void;
  useCompactWidth?: boolean;
}

/**
 * Duration of the search field expand/collapse animation in milliseconds.
 * Used to synchronize visibility timing in parent components.
 * This is ignored when prefers-reduced-motion is enabled.
 */
export const SEARCH_TRANSITION_DURATION_MS = 300;

const COMPACT_WIDTH = 240;
const DEFAULT_WIDTH = 382;

export const SearchFieldTransition = styled.div<{
  expanded: boolean;
  useCompactWidth: boolean;
}>`
  transition: width ${SEARCH_TRANSITION_DURATION_MS}ms,
    background ${SEARCH_TRANSITION_DURATION_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) =>
    props.expanded
      ? css`
          width: ${props.useCompactWidth ? COMPACT_WIDTH : DEFAULT_WIDTH}px;
        `
      : css`
          width: 32px;
        `}
`;

const findShortcut: KeyboardShortcut = {
  modifiers: [isApple ? 'metaKey' : 'ctrlKey'],
  code: 'KeyF',
};
const blurShortcut: KeyboardShortcut = { code: 'Escape' };

const QuickSearch: FunctionComponent<QuickSearchProps> = ({
  onChange: onChangeProp,
  value: valueProp,
  tooltip: IconTooltip,
  onExpandedChange,
  useCompactWidth = false,
}) => {
  const [value, setLocalValue] = useState(valueProp);
  const debouncedOnChanged = useDebouncedCallback(onChangeProp, 500);
  const isEmpty = !value;
  const isDatabaseContentWrapperEnabled = isFeatureFlagEnabled(
    'databases-frontend-content-wrapper'
  );
  const expandedChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * The local value should be updated immediately, but the callback
   * to trigger the search should be debounced.
   */
  const updateValue = useCallback(
    (value?: string) => {
      setLocalValue(value);
      debouncedOnChanged(value);
    },
    [setLocalValue, debouncedOnChanged]
  );

  /**
   * Sync local search field state with external changes to current
   * view settings (changing views, etc)
   */
  useEffect(() => {
    setLocalValue(valueProp);
    if (!valueProp) {
      setExpanded(false);
    }
  }, [valueProp]);

  const searchFieldRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(!isEmpty);
  const isReducedMotion = useIsReducedMotion();

  /**
   * Notify about expansion state changes with intelligent timing:
   * - When expanding: Fire immediately (hide labels right away)
   * - When collapsing: Delay until after animation (show labels after transition)
   * This prevents horizontal scrollbar flicker during collapse animation.
   */
  useEffect(() => {
    // clear any pending timeout from previous state changes
    if (expandedChangeTimeoutRef.current !== null) {
      clearTimeout(expandedChangeTimeoutRef.current);

      expandedChangeTimeoutRef.current = null;
    }

    if (expanded) {
      // Expanding: notify immediately so labels hide right away
      onExpandedChange?.(true);
    } else {
      // Collapsing: delay notification until after transition completes
      const delay = isReducedMotion ? 0 : SEARCH_TRANSITION_DURATION_MS + 20;

      expandedChangeTimeoutRef.current = setTimeout(() => {
        onExpandedChange?.(false);
        expandedChangeTimeoutRef.current = null;
      }, delay);
    }

    return () => {
      if (expandedChangeTimeoutRef.current !== null) {
        clearTimeout(expandedChangeTimeoutRef.current);
      }
    };
  }, [expanded, isReducedMotion, onExpandedChange]);

  const { formatMessage } = useIntl();

  const selectionsActions = getService('SelectionsActions');
  const { hidePanel } = usePanel();
  const panelManager = container.resolve('PanelManager');
  const { panel: activePanel } = useSnapshot(panelManager.state);
  const isPanelActive = activePanel !== null;

  useEffect(() => {
    if (useCompactWidth && isPanelActive) {
      setExpanded(false);
    }
  }, [useCompactWidth, isPanelActive]);

  const handleExpandField = useCallback(() => {
    selectionsActions.deselectAll();
    if (useCompactWidth) {
      hidePanel();
    }
    setExpanded(true);
  }, [selectionsActions, hidePanel, useCompactWidth]);

  const handleFocus = useCallback(() => {
    searchFieldRef.current?.select();
  }, []);

  const handleBlur = useCallback(() => {
    isEmpty && setExpanded(false);
  }, [isEmpty]);

  const handleEscape = useCallback(() => {
    searchFieldRef.current?.blur();
  }, []);

  const handleClear = useCallback(() => {
    updateValue(undefined);
    searchFieldRef.current?.focus();
  }, [updateValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!expanded) {
        handleExpandField();
      }

      const value = e.target.value;

      updateValue(value);
    },
    [expanded, handleExpandField, updateValue]
  );

  useKeyboardShortcut(expanded ? blurShortcut : undefined, handleEscape);
  useKeyboardShortcut(!expanded ? findShortcut : undefined, handleExpandField);
  useKeyboardShortcut(expanded && !isEmpty ? findShortcut : undefined, handleFocus);

  return (
    <SearchFieldTransition expanded={expanded} useCompactWidth={useCompactWidth}>
      {expanded ? (
        <SearchBar
          searchFieldRef={searchFieldRef}
          value={value ?? ''}
          handleOnChange={handleChange}
          handleOnClear={handleClear}
          placeholder={formatMessage(messages['quick-search.search-database-text'])}
          autoComplete="off"
          autoFocus
          onBlur={handleBlur}
        />
      ) : (
        <IconTooltip>
          <IconButton
            icon={(iconProps) => <SearchIcon {...iconProps} size="medium" />}
            label={formatMessage(messages['quick-search.search-database-text'])}
            onClick={handleExpandField}
            appearance={isDatabaseContentWrapperEnabled ? 'subtle' : 'default'}
            spacing="default"
          />
        </IconTooltip>
      )}
    </SearchFieldTransition>
  );
};

export default QuickSearch;
